// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import { FORTA_BLACK, FORTA_ERROR, FORTA_GRAY2 } from '../../../../../../../../../utils/colors';
import { FHSelect } from '../../../../../../../../base';
import NumericInput from '../../../numericInput';
import ErrorBox from '../../../errorBox';
import { goalActions } from '../../../../../../../../../store';
import { capitalizeFirstLetter } from '../../../../../../../../../helpers';
import { BackgroundTypes } from '../../../../../../../../../constants';
import { FREQUENCY_OPTIONS } from '../common/constants/general';
import { validateV2SAGMasteryCriteria } from '../../../../../common/utils/general';

import type {
  ITreatmentPlanGoal,
  ISAGMasteryCriteria,
  SelectValueType,
} from '../../../../../../../../../utils/types';

interface ISAGPercentageMasteryCriteriaState {
  total_attempts: number | null;
  desired_amount: number | null;
  practice_duration_count: number | null;
}

interface IPercentageMasteryCriteria {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
}

interface ISAGPercentageMCRowProps {
  title: 'acquisition' | 'generalization' | 'maintenance';
  isEditing: boolean;
  goalIndex: number;
  total_attempts?: number | null;
  values: ISAGPercentageMasteryCriteriaState;
  showFrequency?: boolean;
  maintenance_frequency?: string | null;
  isEditable: boolean;
  updateMaintenanceFrequency?: (value: string) => void;
  onUpdate: (key: string, value: string | number | null) => void;
}

function isSAGMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ISAGMasteryCriteria {
  return Boolean(
    criteria &&
      'acquisition' in criteria &&
      'maintenance_frequency' in criteria &&
      criteria.acquisition !== null &&
      'total_attempts' in criteria.acquisition,
  );
}

function SAGPercentageMCRow({
  isEditing,
  isEditable,
  title,
  goalIndex,
  values,
  total_attempts = null,
  showFrequency = false,
  maintenance_frequency = null,
  updateMaintenanceFrequency,
  onUpdate,
}: ISAGPercentageMCRowProps) {
  const errors = validateV2SAGMasteryCriteria(values, total_attempts, title);
  const hasErrors = Object.keys(errors).length > 0;
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: `1px solid ${hasErrors ? FORTA_ERROR : FORTA_GRAY2}`,
        borderRadius: '12px',
      }}
    >
      <Typography textAlign="left" fontWeight={700}>
        {capitalizeFirstLetter(title)}
      </Typography>
      {hasErrors && (
        <Typography textAlign="left" color={FORTA_ERROR} mt={2} mb={1}>
          {errors.desired_amount || errors.practice_duration_count}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography textAlign="left" display="inline" sx={{ mr: 1 }}>
          Client will correctly respond in
        </Typography>
        {isEditing ? (
          <NumericInput
            testId={`sagMCDesiredLevel-${goalIndex}-${title}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
            }}
            disabled={!isEditable}
            value={values.desired_amount}
            onChange={(value) => {
              onUpdate('desired_amount', value ? parseInt(value) : null);
            }}
            error={!values.desired_amount || Boolean(errors.desired_amount)}
          />
        ) : values.desired_amount ? (
          <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
            {values.desired_amount}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
          {`out of `}
        </Typography>
        <Typography
          textAlign="left"
          display="inline"
          sx={{ mx: 1 }}
          fontWeight={700}
          color={total_attempts ? FORTA_BLACK : FORTA_ERROR}
        >
          {total_attempts ? total_attempts : '?'}
        </Typography>
        <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
          {` opportunities over`}
        </Typography>
        {isEditing ? (
          <NumericInput
            testId={`masterCriteriaPracticeDurationCount-${goalIndex}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
            }}
            disabled={!isEditable}
            value={values.practice_duration_count}
            onChange={(value) => {
              onUpdate('practice_duration_count', value ? parseInt(value) : null);
            }}
            error={!values.practice_duration_count || Boolean(errors.practice_duration_count)}
          />
        ) : values.practice_duration_count ? (
          <Typography textAlign="left" display="inline" sx={{ mx: 1 }}>
            {values.practice_duration_count}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: 0.5 }}>
          consecutive sessions.
        </Typography>
      </Box>
      {showFrequency && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            my: 1,
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Frequency
          </Typography>
          {isEditing ? (
            <FHSelect
              data-testid={`maintenanceFrequencyDropDown`}
              sx={{ minWidth: 200 }}
              inputHeight={40}
              backgroundType={BackgroundTypes.Colored}
              displayEmpty
              disabled={!isEditable}
              renderValue={
                maintenance_frequency === null
                  ? () => <Typography>Select frequency</Typography>
                  : undefined
              }
              options={FREQUENCY_OPTIONS}
              optionDataTestIdPrefix="maintenanceFrequencyOption"
              value={maintenance_frequency || ''}
              onUpdate={(value: SelectValueType) => {
                if (updateMaintenanceFrequency) {
                  updateMaintenanceFrequency(String(value));
                }
              }}
              error={!maintenance_frequency}
            />
          ) : (
            <Typography align="left">
              {FREQUENCY_OPTIONS.find((option) => option.value === maintenance_frequency)?.label ||
                ''}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function SAGPercentageMasteryCriteriaV2({
  goal,
  goalIndex,
  isEditable,
}: IPercentageMasteryCriteria) {
  const dispatch = useDispatch();

  const { mastery_criteria, measurement_units } = goal;
  const sagMasteryCriteria = isSAGMasteryCriteria(mastery_criteria) ? mastery_criteria : null;
  const { acquisition, generalization, maintenance, maintenance_frequency } =
    sagMasteryCriteria ?? {};
  const total_attempts =
    measurement_units && 'total_attempts' in measurement_units
      ? measurement_units.total_attempts
      : undefined;

  const [acquisitionMCState, setAcquisitionMCState] = useState<ISAGPercentageMasteryCriteriaState>({
    total_attempts: acquisition?.total_attempts ?? null,
    desired_amount: acquisition?.desired_amount ?? null,
    practice_duration_count: acquisition?.practice_duration_count ?? null,
  });
  const [generalizationMCState, setGeneralizationMCState] =
    useState<ISAGPercentageMasteryCriteriaState>({
      total_attempts: generalization?.total_attempts ?? null,
      desired_amount: generalization?.desired_amount ?? null,
      practice_duration_count: generalization?.practice_duration_count ?? null,
    });
  const [maintenanceMCState, setMaintenanceMCState] = useState<ISAGPercentageMasteryCriteriaState>({
    total_attempts: maintenance?.total_attempts ?? null,
    desired_amount: maintenance?.desired_amount ?? null,
    practice_duration_count: maintenance?.practice_duration_count ?? null,
  });
  const [maintenanceFrequency, setMaintenanceFrequency] = useState(maintenance_frequency);
  const [isAcquisitionLoaded, setIsAcquisitionLoaded] = useState(Boolean(acquisition));

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (acquisition && !isAcquisitionLoaded) {
      setIsAcquisitionLoaded(true);
    }
  }, [acquisition]);

  useEffect(() => {
    const { mastery_criteria } = goal;
    const sagMasteryCriteria = isSAGMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { acquisition, generalization, maintenance, maintenance_frequency } =
      sagMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setAcquisitionMCState({
      total_attempts: acquisition?.total_attempts ?? null,
      desired_amount: acquisition?.desired_amount ?? null,
      practice_duration_count: acquisition?.practice_duration_count ?? null,
    });
    setGeneralizationMCState({
      total_attempts: generalization?.total_attempts ?? null,
      desired_amount: generalization?.desired_amount ?? null,
      practice_duration_count: generalization?.practice_duration_count ?? null,
    });
    setMaintenanceMCState({
      total_attempts: maintenance?.total_attempts ?? null,
      desired_amount: maintenance?.desired_amount ?? null,
      practice_duration_count: maintenance?.practice_duration_count ?? null,
    });
    setMaintenanceFrequency(maintenance_frequency);
  }, [goal?.is_editing, isAcquisitionLoaded, goal?.id]);

  const handleUpdateGeneralizationMCState = (key: string, value: string | number | null) => {
    setGeneralizationMCState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  const handleUpdateAcquisitionMCState = (key: string, value: string | number | null) => {
    setAcquisitionMCState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  const handleUpdateMaintenanceMCState = (key: string, value: string | number | null) => {
    setMaintenanceMCState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  const handleUpdateRedux = (
    acquisitionMCState: ISAGPercentageMasteryCriteriaState,
    generalizationMCState: ISAGPercentageMasteryCriteriaState,
    maintenanceMCState: ISAGPercentageMasteryCriteriaState,
    maintenanceFrequency: string | null,
  ) => {
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: {
          acquisition: acquisitionMCState,
          generalization: generalizationMCState,
          maintenance: maintenanceMCState,
          maintenance_frequency: maintenanceFrequency,
        },
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  };

  const debounceReduxUpdate = useCallback(debounce(handleUpdateRedux, 800), []);

  useEffect(() => {
    if (!isDirty) return;
    debounceReduxUpdate(
      acquisitionMCState,
      generalizationMCState,
      maintenanceMCState,
      maintenanceFrequency,
    );
  }, [acquisitionMCState, generalizationMCState, maintenanceMCState, maintenanceFrequency]);

  return (
    <>
      <SAGPercentageMCRow
        title="acquisition"
        onUpdate={handleUpdateAcquisitionMCState}
        goalIndex={goalIndex}
        isEditing={Boolean(goal?.is_editing)}
        values={acquisitionMCState}
        total_attempts={total_attempts}
        isEditable={isEditable}
      />
      <SAGPercentageMCRow
        title="generalization"
        onUpdate={handleUpdateGeneralizationMCState}
        goalIndex={goalIndex}
        isEditing={Boolean(goal?.is_editing)}
        values={generalizationMCState}
        total_attempts={total_attempts}
        isEditable={isEditable}
      />
      <SAGPercentageMCRow
        title="maintenance"
        onUpdate={handleUpdateMaintenanceMCState}
        goalIndex={goalIndex}
        isEditing={Boolean(goal?.is_editing)}
        values={maintenanceMCState}
        showFrequency
        total_attempts={total_attempts}
        maintenance_frequency={maintenanceFrequency}
        updateMaintenanceFrequency={(val) => {
          setMaintenanceFrequency(val);
          setIsDirty(true);
        }}
        isEditable={isEditable}
      />
    </>
  );
}

export default SAGPercentageMasteryCriteriaV2;
